// Import JS Modules
import { builderDispatch, WebUI, PrismUI } from '../../_utils/ui';
import fetchImage from '../../_utils/fetch-image';
import PrintsBuilderPhotoAlbumDirectLoader from './upload-photo-mixin/from-album-to-prints-loader';
import LoaderAdditionalOptions from '../../_photo-loader/additional-options';
import SizeHelpers from '../common/size-helpers';
// This file contains functions that are common to the prints builder and the mobile prints builder.
// Any change made to those function have to be tested in the mobile component.

const UploadPhotosMixin = {
  handleAddPhotos() {
    const { currentlyLoading } = this.state;

    if (currentlyLoading) {
      return;
    }

    const additionalOptions = {
      [LoaderAdditionalOptions.EXTRA_FILE_TYPES]: ['webp'],
      [LoaderAdditionalOptions.PRINT_SIZES]: this.availableSizesForSelectedDeliveryAndFinish().map(
        size => ({
          key: size.key,
          title: size.title,
          selected: size.key === this.getPrintSizeKeyForNewUpload(),
          default: size.default,
        })
      ),
    };

    builderDispatch.dispatch({
      type: PrismUI.ADD_PHOTOS,
      upload_url: this.props.urls.upload,
      callToActionButtonText: 'Add to prints',
      callToActionButtonEvent: 'event_add_to_prints',
      add_title_text: 'photos will be added to your prints order',
      additionalOptions,
    });
  },
  handleAddPhotosFromGallery() {
    const loader = new PrintsBuilderPhotoAlbumDirectLoader(
      this.props.urls.upload_from_album,
      this.handleUploadBegun,
      this.handleUploadProgress,
      this.handleUploadDone
    );

    loader.listen(this.props.uploadFromGalleryListenerUrl);
  },
  handleUploadBegun(total) {
    this.setState({
      currentlyLoading: {
        index: 0,
        total,
        isComplete() {
          return this.index === this.total;
        },
      },
    });

    this.setWarningBeforeNavigatingAwayDuringUpload();
  },
  handleUploadProgress(photo, source, total) {
    // Important - the `photo` object is later added to the print size array but this reference is
    // maintained and the object mutated later, `setState` is called with other keys but this triggers
    // re-render with the `photo` object at that point
    photo.source = source;
    photo.uploaded = true;

    const { currentlyLoading } = this.state;
    const sizeKey = this.getPrintSizeKeyForNewUpload();

    // Important - state is not updated through `setState` for some reason - instead we have
    // to use `getSizeFromKey` which returns reference to obj which is then mutated
    const size = this.getSizeFromKey(sizeKey);
    size.photos = [].concat(photo, size.photos);

    if (!currentlyLoading) {
      return;
    }

    // delay adding the photo to the sizes photo array
    // until the thumbnail has been pulled to the browser

    preloadImage.call(this, 0, onPreloadSuccess, onPreloadFail);

    // periodic save of order
    if ((currentlyLoading.index + 1) % 10 === 0) {
      this.save();
    }

    function preloadImage(attemptIndex, successCallback, failureCallback) {
      const that = this;
      const urls = ['thumb_url', 'large_url', 'raw_url'];
      const url = urls[attemptIndex];

      if (!photo[url]) {
        failureCallback.call(that, attemptIndex);
        return;
      }

      fetchImage(
        photo[url],
        '',
        function() {
          if (this.width > 600 || this.height > 600) {
            resizeCorpulentImage(this, url => {
              successCallback.call(that, url);
            });
          } else {
            successCallback.call(that, this.src);
          }
        },
        () => {
          failureCallback.call(that, attemptIndex);
        }
      );
    }

    function resizeCorpulentImage(image, callback) {
      let w;
      let h;
      if (image.width > image.height) {
        w = 600;
        h = (600 * image.height) / image.width;
      } else {
        h = 600;
        w = (600 * image.width) / image.height;
      }

      const canvas = document.createElement('canvas');
      let ctx;
      canvas.width = w;
      canvas.height = h;
      ctx = canvas.getContext('2d');
      ctx.scale(w / image.width, w / image.width);
      ctx.drawImage(image, 0, 0);
      if (typeof canvas.toBlob === 'function') {
        canvas.toBlob(blob => {
          const url = (window.URL || window.webkitURL).createObjectURL(blob);
          callback(url);
        }, 'image/jpeg');
      } else {
        callback(canvas.toDataURL('image/jpeg'));
      }
    }

    function onPreloadSuccess(src) {
      photo.thumb_url = src;
      pushIntoFirstAvailablePhotoPlaceHolder.call(this, 'raw_url', photo);

      incrementCurrentlyLoadingIndex.call(this);
    }

    function onPreloadFail(attemptIndex) {
      if (attemptIndex < 3) {
        preloadImage.call(
          this,
          attemptIndex + 1,
          onPreloadSuccess,
          onPreloadFail
        );
      } else {
        incrementCurrentlyLoadingIndex.call(this);
      }
    }

    function incrementCurrentlyLoadingIndex() {
      const { currentlyLoading } = this.state;
      if (!currentlyLoading) {
        return;
      }

      ++currentlyLoading.index;
      this.setState(
        {
          currentlyLoading,
        },
        function() {
          if (currentlyLoading.isComplete()) {
            this.setState({
              currentlyLoading: undefined,
            });

            this.uploadCompleted();
          }
        }
      );
    }

    function pushIntoFirstAvailablePhotoPlaceHolder(indicativeKey, newValue) {
      this.state.sizes.forEach(size => {
        for (let i = 0; i < size.photos.length; i++) {
          if (!(indicativeKey in size.photos[i])) {
            if (size.photos[i].quantity) {
              newValue.quantity = size.photos[i].quantity;
            }

            Object.assign(size.photos[i], newValue);
            break;
          }
        }
      });
    }

    function uploadCompletedIfFinished() {
      const { currentlyLoading } = this.state;
      if (!currentlyLoading) return;

      if (currentlyLoading.isComplete()) {
        this.setState({
          currentlyLoading: undefined,
        });

        this.uploadCompleted();
      }
    }
  },
  handleUploadDone(number_of_files, number_of_errors, message) {
    if (number_of_errors) {
      this.save();

      // conventional upload
      builderDispatch.delayedDispatch({
        type: WebUI.UPLOAD_SHOW_FAILURES,
        message,
      });
    }
  },
  handleUploadPaused() {
    const { currentlyLoading } = this.state;
    if (!currentlyLoading) return;

    this.setState({
      currentlyLoading: { ...currentlyLoading, paused: true },
    });
  },
  handleUploadResumed() {
    const { currentlyLoading } = this.state;
    if (!currentlyLoading) return;

    this.setState({
      currentlyLoading: { ...currentlyLoading, paused: false },
    });
  },
  handleAbortedUpload() {
    this.setState(
      {
        currentlyLoading: undefined,
      },
      function() {
        this.uploadCompleted();
      }
    );
  },
  removeIncompleteUploads() {
    const sizes = $.extend([], this.state.sizes);

    sizes.forEach(size => {
      let i = size.photos.length - 1;
      let photo;

      while (i >= 0) {
        photo = size.photos[i];

        if (!('raw_url' in photo) && !('uploaded' in photo)) {
          size.photos.splice(i, 1);
        }
        i--;
      }
    });

    this.setState({
      sizes,
    });
  },
  uploadCompleted(callback) {
    // you may depart
    this.removeIncompleteUploads();

    builderDispatch.delayedDispatch({
      type: PrismUI.IMAGES_UPDATED,
    });

    const finaliseIntervalID = setInterval(() => {
      if (!this.saving) {
        this.saveHard(() => {
          this.clearWarningBeforeNavigatingAwayDuringUpload();
          clearInterval(finaliseIntervalID);

          if (typeof callback === 'function') callback();
        });
      }
    }, 100);
  },

  setWarningBeforeNavigatingAwayDuringUpload() {
    this.setBeforeUnload(
      'Photo uploading in progress. If you leave this page now your upload will be incomplete.'
    );
  },
  clearWarningBeforeNavigatingAwayDuringUpload() {
    this.setBeforeUnload();
  },
  setBeforeUnload(message) {
    if ('onbeforeunload' in window) {
      if (message) {
        window.onbeforeunload = function() {
          return message;
        };
      } else {
        window.onbeforeunload = undefined;
      }
    } else if (message) {
      $(document).on('click', 'a:not([href^="#"])', showWarningMessage);
    } else {
      $(document).off('click', 'a:not([href^="#"])', showWarningMessage);
    }

    function showWarningMessage(e) {
      if (
        !confirm(
          'Photo uploading in progress. If you leave this page now your upload will be incomplete.'
        )
      ) {
        e.preventDefault();
      }
    }
  },
  getPrintSizeKeyForNewUpload() {
    const { viewingSizeKey, finish } = this.state;

    // Try uploading via the mobile filtered size
    if (viewingSizeKey && viewingSizeKey.includes(finish.name)) {
      return viewingSizeKey;
    }

    // Try uploading via the selected uploader size
    if (this.selectedSizeKey?.includes(finish.name)) {
      return this.selectedSizeKey;
    }

    const availableSizes = this.availableSizesForSelectedDeliveryAndFinish();

    // Try uploading via the default size otherwise first available size
    return (
      SizeHelpers.defaultSize(availableSizes)?.key || availableSizes[0].key
    );
  },
};

export default UploadPhotosMixin;
